export type NotificationType = 'file_uploaded' | 'task_status_changed' | 'project_status_changed';

export interface Notification {
  id: string;
  clientId: string;
  clientEmail: string;
  type: NotificationType;
  message: string;
  projectNumber: string;
  projectName: string;
  createdAt: string;
  read: boolean;
}

const STORAGE_KEY = 'admin_notifications';

// Email sending via Supabase Edge Function + Amazon SES
async function sendEmailViaSES(notification: Notification, clientName: string): Promise<void> {
  const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

  // Skip if Supabase is not configured
  if (!supabaseUrl || !supabaseAnonKey) {
    console.warn('Supabase not configured. Notification stored but email not sent.');
    return;
  }

  const typeLabels: Record<NotificationType, string> = {
    file_uploaded: 'New Document Uploaded',
    task_status_changed: 'Task Status Updated',
    project_status_changed: 'Project Status Updated',
  };

  const portalLink = `${window.location.origin}${__BASE_PATH__}/portal`;

  const payload = {
    to_email: notification.clientEmail,
    client_name: clientName,
    notification_type: typeLabels[notification.type],
    message: notification.message,
    project_name: notification.projectName,
    project_number: notification.projectNumber,
    portal_link: portalLink,
  };

  try {
    const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    console.log('Email notification sent successfully to', notification.clientEmail);
  } catch (error) {
    console.error('Failed to send email notification:', error);
  }
}

export async function addNotification(
  payload: Omit<Notification, 'id' | 'createdAt' | 'read'>,
  clientName: string
): Promise<Notification> {
  const newNotification: Notification = {
    ...payload,
    id: `notif_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    createdAt: new Date().toISOString(),
    read: false,
  };

  const stored = localStorage.getItem(STORAGE_KEY);
  const allNotifications: Notification[] = stored ? JSON.parse(stored) : [];
  allNotifications.push(newNotification);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(allNotifications));

  // Send email notification via Supabase Edge Function
  await sendEmailViaSES(newNotification, clientName);

  return newNotification;
}

export function getNotificationsForClient(clientId: string): Notification[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  const allNotifications: Notification[] = stored ? JSON.parse(stored) : [];
  return allNotifications
    .filter((n) => n.clientId === clientId)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
}

export function markAsRead(notificationId: string): void {
  const stored = localStorage.getItem(STORAGE_KEY);
  const allNotifications: Notification[] = stored ? JSON.parse(stored) : [];
  const updated = allNotifications.map((n) =>
    n.id === notificationId ? { ...n, read: true } : n
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
}

export function markAllAsRead(clientId: string): void {
  const stored = localStorage.getItem(STORAGE_KEY);
  const allNotifications: Notification[] = stored ? JSON.parse(stored) : [];
  const updated = allNotifications.map((n) =>
    n.clientId === clientId ? { ...n, read: true } : n
  );
  localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
}